'use client';

import AuthPanel from '@/components/auth/AuthPanel';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useSignOut } from '@/hooks/use-sign-out';
import { isProtectedRoute } from '@/utils/navigation';
import { CheckVerifySessionResponse } from '@/utils/type';
import { generateDeviceFingerprint } from '@/utils/deviceFingerprint';
import { Modal } from '@mantine/core';
import { usePathname, useRouter } from 'next/navigation';
import { createContext, Dispatch, ReactNode, useCallback, useContext, useEffect, useReducer } from 'react';

type AuthAction = {
  type: 'OPEN_MODAL' | 'CLOSE_MODAL' | '2FA_ACCEPT' | '2FA_REJECT' | 'PRIVACY_POLICY_ACCEPTED';
  [prop: string]: any;
};

export type AuthModalTab = 'login' | 'signup' | 'resetpass' | 'verify-otp';

type AuthContextState = {
  modalOpened: boolean;
  defaultTab: AuthModalTab;
  twoFactorAuthenticated: boolean;
  sessionChecked: boolean;
  isExplicitLogin: boolean;
  isFirstLogin: boolean;
  isVerified: boolean;
  isTrustedDevice: boolean;
  privacyPolicyAccepted: boolean;
  skipSessionCheck: boolean; // Flag to prevent immediate session check after auth
}

const initialState: AuthContextState = {
  modalOpened: false,
  defaultTab: 'login' as AuthModalTab,
  twoFactorAuthenticated: false,
  sessionChecked: false,
  isExplicitLogin: false,
  isFirstLogin: false,
  isVerified: false,
  isTrustedDevice: false,
  privacyPolicyAccepted: false,
  skipSessionCheck: false,
};

const reducer = (state: AuthContextState, action: AuthAction) => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return {
        ...state,
        modalOpened: true,
        defaultTab: action.payload?.defaultTab ?? 'login',
        isExplicitLogin: action.payload?.defaultTab === 'login',
        isFirstLogin: action.payload?.isFirstLogin ?? false,
      };
    case 'CLOSE_MODAL':
      return {
        ...state,
        modalOpened: false,
        sessionChecked: true
      };
    case '2FA_ACCEPT':
      return {
        ...state,
        twoFactorAuthenticated: true,
        sessionChecked: true,
        modalOpened: false,
        isVerified: true,
        isTrustedDevice: action.payload?.isTrustedDevice || false,
        skipSessionCheck: true // Skip session check after successful auth
      };
    case '2FA_REJECT':
      return {
        ...state,
        twoFactorAuthenticated: false,
        sessionChecked: true, // We have checked the session and determined 2FA is required
        isVerified: false
      };
    case 'PRIVACY_POLICY_ACCEPTED':
      return {
        ...state,
        privacyPolicyAccepted: true
      };
    default:
      return state;
  }
};

type AuthContextType = {
  state: AuthContextState;
  dispatch: Dispatch<AuthAction>
}

const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [currentUser, authStatus] = useCurrentUser();
  const router = useRouter();
  const pathname = usePathname();
  const signOut = useSignOut();

  const checkSession = useCallback(async () => {
    try {
      if (!currentUser) {
        dispatch({ type: '2FA_REJECT' });
        return;
      }

      // CRITICAL: Check email verification first
      if (!currentUser.emailVerified) {
        // For email/password users, email must be verified
        const isEmailPasswordUser = currentUser.providerData.some(
          provider => provider.providerId === 'password'
        );

        if (isEmailPasswordUser) {
          console.log('❌ Email not verified - blocking access');
          dispatch({ type: '2FA_REJECT' });

          // Sign out unverified users and show signup modal
          await signOut();

          if (isProtectedRoute(pathname)) {
            dispatch({
              type: 'OPEN_MODAL',
              payload: {
                defaultTab: 'signup'
              }
            });
          }
          return;
        }
      }

      // Check if user is authenticated through Google
      const isGoogleUser = currentUser.providerData[0]?.providerId === 'google.com';

      if (isGoogleUser) {
        dispatch({
          type: '2FA_ACCEPT',
          payload: {
            isTrustedDevice: true,
            isVerified: true
          }
        });
        return;
      }

      const token = await currentUser.getIdToken();

      // Generate device fingerprint to check trusted devices
      const deviceFingerprint = generateDeviceFingerprint();
      console.log(`[Auth] Checking session with device fingerprint: ${deviceFingerprint}`);

      // Verify the session with device fingerprint
      const response = await fetch('/api/auth/verify-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deviceFingerprint
        })
      });

      if (!response.ok) {
        dispatch({ type: '2FA_REJECT' });
        if (isProtectedRoute(pathname)) {
          router.push("/");
        }
        return;
      }

      const checkSessionResponse = await response.json() as CheckVerifySessionResponse;

      if (checkSessionResponse.requiresLogin) {
        dispatch({ type: '2FA_REJECT' });
        if (isProtectedRoute(pathname)) {
          router.push("/");
        }
      } else if (checkSessionResponse.requiresTwoFactor) {
        dispatch({ type: '2FA_REJECT' });
        // IMPORTANT: Show modal regardless of route because 2FA is required
        dispatch({
          type: 'OPEN_MODAL',
          payload: {
            defaultTab: 'verify-otp',
            isFirstLogin: false
          }
        });
      } else {
        dispatch({
          type: '2FA_ACCEPT',
          payload: {
            isTrustedDevice: false,
            isVerified: true
          }
        });
      }
    } catch (error) {
      console.error('Error checking session:', error);
      dispatch({ type: '2FA_REJECT' });
      if (isProtectedRoute(pathname)) {
        router.push("/");
      }
    }
  }, [currentUser, dispatch, pathname, router]);

  // Check session on mount and when user changes
  useEffect(() => {
    if (currentUser && !state.sessionChecked && !state.skipSessionCheck) {
      checkSession();
    }
  }, [currentUser, state.sessionChecked, state.skipSessionCheck, checkSession]);

  // Removed localStorage-based OTP check - now handled by server-side device fingerprint

  // Handle modal state changes
  useEffect(() => {
    if (state.modalOpened && currentUser) {
      checkSession();
    }
  }, [state.modalOpened, currentUser, checkSession]);

  // CRITICAL: Protect routes when 2FA is required but not completed
  useEffect(() => {
    if (currentUser && !state.twoFactorAuthenticated && state.sessionChecked && isProtectedRoute(pathname)) {
      console.log('⚠️ [Auth] User on protected route without 2FA completion - redirecting to home');
      router.push('/');
    }
  }, [currentUser, state.twoFactorAuthenticated, state.sessionChecked, pathname, router]);

  return (
    <AuthContext.Provider value={{ state, dispatch }}>
      {children}
      <Modal
        centered
        size={450}
        padding={0}
        radius={20}
        withCloseButton={false}
        opened={state.modalOpened}
        closeOnClickOutside={false}
        closeOnEscape={false}
        onClose={() => {
          dispatch({ type: 'CLOSE_MODAL' });
        }}
      >
        <AuthPanel
          defaultTab={state.defaultTab}
          onCloseRequest={() => {
            dispatch({ type: 'CLOSE_MODAL' });
          }}
        />
      </Modal>
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const authContext = useContext(AuthContext);
  if (authContext === null) {
    throw new Error("Must use inside auth context provider.");
  }

  return authContext;
}
